/**
 * TodoCompletedSection - Collapsible "Done (N)" section for completed tasks.
 * Tapping a completed task uncompletes it.
 */

import React, { useCallback, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  withTiming,
  useSharedValue,
} from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';

import { IconSymbol } from '@/components/ui/icon-symbol';
import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { Task } from '@/types/tasks';

type TodoCompletedSectionProps = {
  tasks: Task[];
  onToggle: (taskId: string) => void;
};

export function TodoCompletedSection({ tasks, onToggle }: TodoCompletedSectionProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const [expanded, setExpanded] = useState(false);
  const rotation = useSharedValue(0);

  const toggleExpanded = useCallback(() => {
    const next = !expanded;
    setExpanded(next);
    rotation.value = withTiming(next ? 180 : 0, { duration: 200 });
  }, [expanded, rotation]);

  const chevronStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }));

  const handleToggle = useCallback((taskId: string) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onToggle(taskId);
  }, [onToggle]);

  if (tasks.length === 0) return null;

  return (
    <View style={styles.container}>
      <Pressable onPress={toggleExpanded} style={styles.header}>
        <Animated.View style={chevronStyle}>
          <IconSymbol name="chevron.right" size={16} color={palette.textMuted} />
        </Animated.View>
        <Text style={[styles.headerText, { color: palette.textMuted }]}>
          Done ({tasks.length})
        </Text>
      </Pressable>

      {expanded && (
        <View style={styles.list}>
          {tasks.map(task => (
            <Pressable
              key={task.id}
              onPress={() => handleToggle(task.id)}
              style={[styles.taskRow, { backgroundColor: palette.cardBackground }]}
            >
              <View style={[styles.checkboxFilled, { backgroundColor: palette.primary }]}>
                <IconSymbol
                  name="checkmark"
                  size={12}
                  color={colorScheme === 'dark' ? '#000000' : '#FFFFFF'}
                />
              </View>
              <Text
                style={[styles.taskTitle, { color: palette.textMuted }]}
                numberOfLines={1}
              >
                {task.title}
              </Text>
            </Pressable>
          ))}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: Spacing.lg,
    paddingHorizontal: Spacing.lg,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    paddingVertical: Spacing.md,
  },
  headerText: {
    fontSize: 14,
    fontWeight: '600',
  },
  list: {
    gap: Spacing.xxs,
  },
  taskRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderRadius: BorderRadius.sm,
  },
  checkboxFilled: {
    width: 20,
    height: 20,
    borderRadius: BorderRadius.full,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: Spacing.md,
  },
  taskTitle: {
    flex: 1,
    fontSize: 15,
    textDecorationLine: 'line-through',
  },
});
